import React from "react";
import { LoginFlow, loginErrorMessage } from "./authentication";
import { UserState } from "./db";
import "./Login.scss";
import "./components/Card.scss";

interface LoginProps {
  isActive: boolean;
  onLogin: () => void;
  userState: UserState;
  CTA?: string | null;
}
type Step = "providers" | "email" | "create" | "reset" | "resetSent";
interface LoginState {
  step: Step;
  email: string;
  password: string;
  error: string | null;
  busy: boolean;
  isLoginPending: boolean;
}
export default class Login extends React.PureComponent<LoginProps, LoginState> {
  state: LoginState = { step: "providers", email: "", password: "", error: null, busy: false, isLoginPending: false };
  private flow: LoginFlow | null = null;
  private mounted = false;
  private inFlight = false;
  componentDidMount() { this.mounted = true; }
  componentWillUnmount() { this.mounted = false; }
  componentDidUpdate(previous: LoginProps) {
    if (previous.userState === "registered" && this.props.userState !== "registered" && !this.state.busy) {
      this.flow = null;
      this.setState({ step: "providers", email: "", password: "", error: null, isLoginPending: false });
    } else if (previous.userState !== this.props.userState && this.state.isLoginPending && !this.state.busy) this.setState({ isLoginPending: false });
  }
  private getFlow = () => this.flow || (this.flow = new LoginFlow());
  private go = (step: Step) => {
    if (step === "providers") this.flow?.cancelProviderLink();
    this.setState({ step, password: "", error: null });
  };
  private run = async (operation: () => Promise<void>, reset = false) => {
    if (this.inFlight) return;
    this.inFlight = true;
    this.setState({ busy: true, error: null });
    try {
      await operation();
      if (!this.mounted) return;
      this.setState(state => ({ ...state, busy: false, password: "", isLoginPending: !reset, step: reset ? "resetSent" : state.step }));
      if (!reset) this.props.onLogin();
    } catch (error) {
      if (!this.mounted) return;
      this.setState(state => ({ ...state, busy: false, password: "", error: this.flow?.needsCompletion
        ? "You’re signed in, but we couldn’t finish saving your earlier progress. Please retry to finish."
        : loginErrorMessage(error),
        step: this.flow?.providerLinkEmail ? "email" : state.step,
        email: this.flow?.providerLinkEmail || state.email,
      }));
    } finally { this.inFlight = false; }
  };
  private submit = (event: React.FormEvent) => {
    event.preventDefault();
    const flow = this.getFlow();
    const { step, password } = this.state;
    const email = this.state.email.trim();
    if (flow.needsCompletion) return this.run(() => flow.retryCompletion());
    if (step === "reset") return this.run(() => flow.resetPassword(email), true);
    return this.run(() => step === "create" ? flow.createEmail(email, password) : flow.signInEmail(email, password));
  };
  render() {
    const { isActive, userState, CTA } = this.props;
    const { step, email, password, error, busy, isLoginPending } = this.state;
    const isLoggedIn = (isLoginPending || userState === "registered") && !busy && !error;
    const finishing = Boolean(this.flow?.needsCompletion);
    const linking = Boolean(this.flow?.providerLinkEmail);
    return <div className="LoginWrapper">
      {isLoggedIn ? <div className="LoginConfirmation">
        <div className="LoginConfirmationGlyph" />
        <div>Thank you! Your progress will be saved as you read.</div>
      </div> : <>
        {CTA !== null && <div className="LoginCTA">{CTA || "Please sign in so we can save your progress and let you know the best times to review."}</div>}
        {isActive && <div className="LoginContent" aria-busy={busy}>
          {error && <p className="LoginError" role="alert">{error}</p>}
          {finishing ? <button className="LoginPrimary" type="button" disabled={busy} onClick={() => this.run(() => this.getFlow().retryCompletion())}>
            {busy ? "Saving progress…" : "Retry saving progress"}
          </button> : step === "providers" ? <div className="LoginProviders">
            <button className="LoginProvider LoginGoogle" type="button" aria-label="Sign in with Google" disabled={busy} onClick={() => this.run(() => this.getFlow().signInGoogle())}>
              <img src="/assets/ui/google.svg" alt="" />Sign in with Google
            </button>
            <button className="LoginProvider LoginEmail" type="button" aria-label="Sign in with email" disabled={busy} onClick={() => this.go("email")}>
              <span aria-hidden="true">✉</span>Sign in with email
            </button>
          </div> : step === "resetSent" ? <div className="LoginResetConfirmation" role="status">
            <p>If there’s an account for that address, you’ll receive an email with a link to reset your password.</p>
            <button className="LoginLink" type="button" onClick={() => this.go("email")}>Back to sign in</button>
          </div> : <form className="LoginForm" onSubmit={this.submit}>
            <h2>{step === "create" ? "Create an account" : step === "reset" ? "Reset your password" : "Sign in with email"}</h2>
            <label>Email<input name="email" type="email" autoComplete="email" required autoFocus value={email} readOnly={linking} disabled={busy}
              onChange={event => this.setState({ email: event.target.value })} /></label>
            {step !== "reset" && <label>Password<input name="password" type="password" required minLength={step === "create" ? 6 : undefined}
              autoComplete={step === "create" ? "new-password" : "current-password"} value={password} disabled={busy}
              onChange={event => this.setState({ password: event.target.value })} /></label>}
            {step === "email" && <button className="LoginLink" type="button" disabled={busy} onClick={() => this.go("reset")}>Forgot password?</button>}
            <div className="LoginActions">
              <button className="LoginPrimary" type="submit" disabled={busy}>{busy ? "Please wait…" : step === "create" ? "Create account" : step === "reset" ? "Send reset email" : "Sign in"}</button>
              <button className="LoginLink" type="button" disabled={busy} onClick={() => this.go(step === "reset" ? "email" : "providers")}>Back</button>
            </div>
            {step !== "reset" && !linking && <button className="LoginLink" type="button" disabled={busy} onClick={() => this.go(step === "create" ? "email" : "create")}>
              {step === "create" ? "Already have an account? Sign in" : "New here? Create an account"}
            </button>}
          </form>}
          {step !== "reset" && step !== "resetSent" && !finishing && <p className="ConsentPreamble">By creating an account via any of the options above, I confirm that I have read, consent and agree to Quantum Country’s <a href="/terms" target="_blank" rel="noopener noreferrer">User Agreement and Privacy Policy</a>, and I am at least 16 years of age.</p>}
          {busy && <span className="LoginBusy" role="status">{finishing ? "Saving progress…" : "Please wait…"}</span>}
        </div>}
      </>}
    </div>;
  }
}
